import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import {
  TRADE_FILTER_SELECT_TRIGGER_CLASS,
  TRADE_LOG_FILTER_SELECT_CONTENT_CLASS,
  TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
} from "@/components/trade-filter-selects";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { AvailableInstrumentRow } from "../../../../types/tradeManagementReport";
import { formatAvailableInstrumentOptionLabel } from "../../../../utils/marketDataSourceChoice";

export type PerformanceReviewSymbolsFieldProps = {
  allSymbols: boolean;
  onAllSymbolsChange: (allSymbols: boolean) => void;
  selectedSymbols: string[];
  onSelectedSymbolsChange: (symbols: string[]) => void;
  instruments: AvailableInstrumentRow[];
  instrumentsLoading?: boolean;
  disabled?: boolean;
};

function sortInstruments(rows: AvailableInstrumentRow[]): AvailableInstrumentRow[] {
  return [...rows].sort((a, b) => a.symbol.localeCompare(b.symbol));
}

export function PerformanceReviewSymbolsField({
  allSymbols,
  onAllSymbolsChange,
  selectedSymbols,
  onSelectedSymbolsChange,
  instruments,
  instrumentsLoading = false,
  disabled = false,
}: PerformanceReviewSymbolsFieldProps): React.ReactElement {
  const sorted = React.useMemo(() => sortInstruments(instruments), [instruments]);
  const selectedSet = React.useMemo(() => new Set(selectedSymbols), [selectedSymbols]);

  const selectedInstrumentRow =
    selectedSymbols.length === 1
      ? sorted.find((row) => row.symbol === selectedSymbols[0])
      : undefined;

  const triggerLabel = instrumentsLoading
    ? "Loading symbols…"
    : sorted.length === 0
      ? "No symbols in scope"
      : allSymbols
        ? "All symbols"
        : selectedSymbols.length === 0
          ? "Choose symbols…"
          : selectedSymbols.length === 1
            ? selectedInstrumentRow
              ? formatAvailableInstrumentOptionLabel(selectedInstrumentRow)
              : selectedSymbols[0]!
            : `${selectedSymbols.length} symbols selected`;

  const selectAllSymbols = () => {
    onAllSymbolsChange(true);
    onSelectedSymbolsChange([]);
  };

  const toggleSymbol = (symbol: string, checked: boolean) => {
    if (checked) {
      onAllSymbolsChange(false);
      if (selectedSet.has(symbol)) return;
      onSelectedSymbolsChange([...selectedSymbols, symbol]);
      return;
    }
    const next = selectedSymbols.filter((value) => value !== symbol);
    onSelectedSymbolsChange(next);
    if (next.length === 0) {
      onAllSymbolsChange(true);
    }
  };

  const triggerTitle = allSymbols
    ? "All symbols in scope"
    : selectedSymbols.length > 0
      ? selectedSymbols.join(", ")
      : undefined;

  const triggerDisabled = disabled || (instrumentsLoading && sorted.length === 0) || sorted.length === 0;
  const showPlaceholder = !allSymbols && selectedSymbols.length === 0 && sorted.length > 0;

  return (
    <div
      data-trade-filter-bar=""
      data-trade-filter-bar-layout="full"
      data-generate-report-wizard-fields=""
      className="w-full min-w-0"
      data-performance-review-symbols=""
    >
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild disabled={triggerDisabled}>
          <button
            type="button"
            role="combobox"
            data-slot="select-trigger"
            data-size="default"
            data-performance-review-symbols-trigger=""
            disabled={triggerDisabled}
            className={cn(TRADE_FILTER_SELECT_TRIGGER_CLASS, "w-full")}
            title={triggerTitle}
          >
            <span
              data-slot="select-value"
              className={cn("min-w-0 flex-1 truncate text-left", showPlaceholder && "text-muted-foreground")}
            >
              {triggerLabel}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          className={cn(
            "z-[200] max-h-56 w-[var(--radix-dropdown-menu-trigger-width)] max-w-sm overflow-y-auto p-1",
            TRADE_LOG_FILTER_SELECT_CONTENT_CLASS
          )}
          data-trade-log-filter-select-content=""
          data-performance-review-symbols-picker=""
        >
          <DropdownMenuCheckboxItem
            checked={allSymbols}
            onCheckedChange={(checked) => {
              if (checked === true) {
                selectAllSymbols();
                return;
              }
              onAllSymbolsChange(false);
            }}
            onSelect={(event) => event.preventDefault()}
            className={cn(
              TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
              "font-[family-name:var(--font-primary)] font-normal",
              "dark:data-highlighted:!bg-sidebar-accent dark:focus:!bg-sidebar-accent"
            )}
          >
            All symbols
          </DropdownMenuCheckboxItem>

          <DropdownMenuSeparator className="my-1" />

          {sorted.map((row) => {
            const isChecked = !allSymbols && selectedSet.has(row.symbol);
            return (
              <DropdownMenuCheckboxItem
                key={row.symbol}
                checked={isChecked}
                onCheckedChange={(checked) => toggleSymbol(row.symbol, checked === true)}
                onSelect={(event) => event.preventDefault()}
                className={cn(
                  TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
                  "font-[family-name:var(--font-primary)] font-normal",
                  "dark:data-highlighted:!bg-sidebar-accent dark:focus:!bg-sidebar-accent"
                )}
              >
                <span className="min-w-0 truncate">
                  {formatAvailableInstrumentOptionLabel(row)}
                </span>
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
